import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PendingIcon from "@mui/icons-material/Pending";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
  Typography
} from "@mui/material";
import React, { useEffect } from "react";

import { usePipelineConfigSync } from "../../../../common/hooks/usePipelineConfigSync";
import { PipelineConfigSyncStatus } from "../../../../common/types/pipelineConfigSync";

const formatSyncTimestamp = (date: Date | null): string => {
  if (!date) {
    return "Never";
  }
  return date.toLocaleString();
};

const statusConfig: Record<PipelineConfigSyncStatus, { label: string; color: "success" | "error" | "warning"; icon: React.ReactElement }> = {
  [PipelineConfigSyncStatus.SUCCESS]: { label: "Success", color: "success", icon: <CheckCircleIcon fontSize="small" /> },
  [PipelineConfigSyncStatus.ERROR]: { label: "Error", color: "error", icon: <ErrorIcon fontSize="small" /> },
  [PipelineConfigSyncStatus.PENDING]: { label: "Pending", color: "warning", icon: <PendingIcon fontSize="small" /> }
};

export const SyncStatusPanel: React.FC = () => {
  const {
    syncPipelineConfig,
    isSyncing,
    syncError,
    lastSyncAt,
    lastSyncStatus,
    refreshSyncStatus
  } = usePipelineConfigSync();

  useEffect(() => {
    refreshSyncStatus();
  }, [refreshSyncStatus]);

  const currentStatus = lastSyncStatus ? statusConfig[lastSyncStatus] : null;
  const errorTooltip = lastSyncStatus === PipelineConfigSyncStatus.ERROR && syncError ? syncError : "";

  return (
    <Box display="flex" alignItems="center" gap={1.5}>
      <Tooltip title="Sync analysis configuration to the processing pipeline" arrow>
        <span>
          <Button
            variant="outlined"
            size="small"
            startIcon={isSyncing ? <CircularProgress size={14} /> : <SyncIcon fontSize="small" />}
            onClick={syncPipelineConfig}
            disabled={isSyncing}
          >
            {isSyncing ? "Syncing..." : "Sync to Pipeline"}
          </Button>
        </span>
      </Tooltip>

      {currentStatus && (
        <Tooltip title={errorTooltip} arrow disableHoverListener={!errorTooltip}>
          <Chip
            icon={currentStatus.icon}
            label={currentStatus.label}
            color={currentStatus.color}
            size="small"
            variant="outlined"
          />
        </Tooltip>
      )}

      <Typography variant="caption" color="text.secondary" noWrap>
        Last synced: {formatSyncTimestamp(lastSyncAt)}
      </Typography>
    </Box>
  );
};
